import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../../api';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils';
import { Spinner } from '../../components/ui';
import {
  Users, Calendar, Star, TrendingUp, TrendingDown, Download,
  BarChart3, Clock, Award, Target, Activity, ChevronUp, ChevronDown,
  Inbox, RefreshCw, AlertCircle, Zap
} from 'lucide-react';

/**
 * AnalyticsPage — Performance Club dark theme.
 *
 * Preserves all functionality of the original (4 stat cards, 2 bar charts,
 * 1 donut, top performers, busiest days, trainer table, CSV export,
 * period selector) but the visual language matches the rest of the
 * Performance Club app: dark surface, cream text, lime accents,
 * translucent borders, hero-kicker / editorial-title / editorial-panel.
 */
export const AnalyticsPage = () => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await analyticsAPI.getDashboard(days);
        setAnalytics(response.data);
      } catch (err) {
        console.error('Failed to load analytics:', err);
        setError(t('analyticsPage.error.load', { defaultValue: 'Could not load analytics.' }));
      } finally {
        setLoading(false);
      }
    };
    loadAnalytics();
  }, [days, t]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await analyticsAPI.exportCSV(days);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gymgamify_analytics_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 text-primary-200 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => setDays(days)} t={t} />;
  }

  if (!analytics) {
    return (
      <div className="text-center py-16" data-testid="analytics-empty">
        <div className="mx-auto h-14 w-14 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center mb-4">
          <Inbox className="w-7 h-7 text-[var(--color-text-muted)]" />
        </div>
        <p className="font-display text-xl text-[var(--color-text)]">
          {t('analyticsPage.empty.title', { defaultValue: 'No analytics data' })}
        </p>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {t('analyticsPage.empty.hint', { defaultValue: 'Start logging sessions to see analytics.' })}
        </p>
      </div>
    );
  }

  const { user_engagement, growth_metrics, trainer_performance, top_performers } = analytics;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span className="hero-kicker">
            {t('analyticsPage.kicker', { defaultValue: 'Operations' })}
          </span>
          <h1 className="mt-2 font-display text-3xl uppercase text-[var(--color-text)]">
            {t('analyticsPage.title', { defaultValue: 'Performance overview' })}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {t('analyticsPage.subtitle', {
              defaultValue: 'Comprehensive insights into your gym — engagement, growth, and trainer output.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={days} onChange={setDays} t={t} />
          <button
            onClick={handleExportCSV}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold hover:from-primary-300 hover:to-primary-200 transition-all disabled:opacity-50"
            data-testid="export-csv"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {exporting
              ? t('analyticsPage.exporting', { defaultValue: 'Exporting…' })
              : t('analyticsPage.export', { defaultValue: 'Export CSV' })}
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label={t('analyticsPage.metricTotalUsers', { defaultValue: 'Total Users' })}
          value={user_engagement?.total_users || 0}
          color="primary"
        />
        <StatCard
          icon={Activity}
          label={t('analyticsPage.metricActiveUsers', { defaultValue: 'Active Users' })}
          value={user_engagement?.active_users || 0}
          trend={growth_metrics?.users_growth_percent >= 0 ? 'up' : 'down'}
          trendValue={Math.abs(growth_metrics?.users_growth_percent || 0)}
          color="blue"
        />
        <StatCard
          icon={Calendar}
          label={t('analyticsPage.metricSessions', { defaultValue: 'Sessions' })}
          value={growth_metrics?.period_sessions || 0}
          trend={growth_metrics?.sessions_growth_percent >= 0 ? 'up' : 'down'}
          trendValue={Math.abs(growth_metrics?.sessions_growth_percent || 0)}
          color="purple"
        />
        <StatCard
          icon={Star}
          label={t('analyticsPage.metricAvgPoints', { defaultValue: 'Avg Points/User' })}
          value={Math.round(user_engagement?.avg_points_per_user || 0).toLocaleString()}
          color="amber"
        />
      </div>

      {/* Growth & Engagement Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Engagement Metrics */}
        <EditorialPanel
          title={t('analyticsPage.engagement.title', { defaultValue: 'Engagement' })}
          kicker={t('analyticsPage.engagement.kicker', { defaultValue: 'Member health' })}
        >
          <div className="space-y-4">
            <MetricBar
              label={t('analyticsPage.engagement.rate', { defaultValue: 'Engagement Rate' })}
              value={`${user_engagement?.engagement_rate || 0}%`}
              percent={user_engagement?.engagement_rate || 0}
              color="primary"
            />
            <MetricBar
              label={t('analyticsPage.engagement.retention', { defaultValue: 'Retention Rate' })}
              value={`${user_engagement?.retention_rate || 0}%`}
              percent={user_engagement?.retention_rate || 0}
              color="blue"
            />
            <MetricBar
              label={t('analyticsPage.engagement.avgStreak', { defaultValue: 'Avg Streak' })}
              value={`${user_engagement?.avg_streak || 0} d`}
              percent={Math.min(((user_engagement?.avg_streak || 0) / 30) * 100, 100)}
              color="purple"
            />
            <MetricBar
              label={t('analyticsPage.engagement.avgSessions', { defaultValue: 'Avg Sessions / User' })}
              value={(user_engagement?.avg_sessions_per_user || 0).toFixed(1)}
              percent={Math.min(((user_engagement?.avg_sessions_per_user || 0) / 20) * 100, 100)}
              color="amber"
            />
          </div>
        </EditorialPanel>

        {/* New Users / Growth */}
        <EditorialPanel
          title={t('analyticsPage.growth.title', { defaultValue: 'New members' })}
          kicker={t('analyticsPage.growth.kicker', { defaultValue: 'Acquisition' })}
        >
          <div className="flex flex-col items-center justify-center py-4">
            <div className="font-display text-6xl text-[var(--color-text)]">
              {user_engagement?.new_users_this_period || 0}
            </div>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              {t('analyticsPage.growth.label', { defaultValue: 'New users this period' })}
            </p>
            <div
              className={cn(
                'inline-flex items-center gap-1 mt-3 px-3 py-1 rounded-full text-sm font-medium border',
                growth_metrics?.users_growth_percent >= 0
                  ? 'border-primary-200/30 bg-primary-200/10 text-primary-100'
                  : 'border-red-500/30 bg-red-500/10 text-red-200'
              )}
            >
              {growth_metrics?.users_growth_percent >= 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              {Math.abs(growth_metrics?.users_growth_percent || 0)}% {t('analyticsPage.growth.vsPrev', { defaultValue: 'vs previous period' })}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--color-border)] grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-2xl font-display text-[var(--color-text)]">
                {growth_metrics?.period_sessions || 0}
              </p>
              <p className="text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mt-1">
                {t('analyticsPage.growth.sessions', { defaultValue: 'Sessions' })}
              </p>
            </div>
            <div>
              <p className="text-2xl font-display text-[var(--color-text-muted)]">
                {growth_metrics?.previous_period_sessions || 0}
              </p>
              <p className="text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mt-1">
                {t('analyticsPage.growth.previous', { defaultValue: 'Previous' })}
              </p>
            </div>
          </div>
        </EditorialPanel>

        {/* Session Types */}
        <EditorialPanel
          title={t('analyticsPage.sessionTypes.title', { defaultValue: 'Session mix' })}
          kicker={t('analyticsPage.sessionTypes.kicker', { defaultValue: 'Format split' })}
        >
          {analytics.session_type_breakdown?.length > 0 ? (
            <DonutChart data={analytics.session_type_breakdown} />
          ) : (
            <div className="flex items-center justify-center h-40 text-[var(--color-text-muted)] text-sm">
              {t('analyticsPage.sessionTypes.empty', { defaultValue: 'No session data' })}
            </div>
          )}
        </EditorialPanel>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EditorialPanel
          title={t('analyticsPage.pointsOverTime', { defaultValue: 'Points earned' })}
          kicker={t('analyticsPage.pointsOverTimeKicker', { defaultValue: 'Last 14 days' })}
          icon={Star}
          iconClass="text-primary-100"
        >
          <BarChart
            data={analytics.points_over_time || []}
            dataKey="total_points"
            labelKey="date"
            color="primary"
            height={200}
          />
        </EditorialPanel>

        <EditorialPanel
          title={t('analyticsPage.sessionFreq', { defaultValue: 'Session frequency' })}
          kicker={t('analyticsPage.sessionFreqKicker', { defaultValue: 'Daily count' })}
          icon={Calendar}
          iconClass="text-primary-100"
        >
          <BarChart
            data={analytics.session_frequency || []}
            dataKey="session_count"
            labelKey="date"
            color="primary"
            height={200}
          />
        </EditorialPanel>
      </div>

      {/* Activity Patterns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Day of Week */}
        <EditorialPanel
          title={t('analyticsPage.busiestDays', { defaultValue: 'Busiest days' })}
          kicker={t('analyticsPage.busiestDaysKicker', { defaultValue: 'By weekday' })}
          icon={Clock}
          iconClass="text-primary-100"
        >
          {analytics.day_of_week_activity?.length > 0 ? (
            <div className="space-y-3">
              {analytics.day_of_week_activity
                .sort((a, b) => b.session_count - a.session_count)
                .map((day, idx) => {
                  const max = Math.max(...analytics.day_of_week_activity.map((d) => d.session_count));
                  return (
                    <div key={day.day} className="flex items-center gap-3" data-testid={`dow-${day.day}`}>
                      <span className="w-20 text-xs uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
                        {day.day.slice(0, 3)}
                      </span>
                      <div className="flex-1 h-6 bg-white/[0.04] rounded-full overflow-hidden border border-[var(--color-border)]">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all duration-500',
                            idx === 0 ? 'bg-gradient-to-r from-primary-500 to-primary-300' : 'bg-primary-200/30'
                          )}
                          style={{ width: `${(day.session_count / max) * 100}%` }}
                        />
                      </div>
                      <span className="w-10 text-sm font-display text-[var(--color-text)] text-right">
                        {day.session_count}
                      </span>
                    </div>
                  );
                })}
            </div>
          ) : (
            <EmptyMini
              icon={Clock}
              text={t('analyticsPage.busiestDaysEmpty', { defaultValue: 'No activity data' })}
            />
          )}
        </EditorialPanel>

        {/* Top Performers */}
        <EditorialPanel
          title={t('analyticsPage.topPerformers', { defaultValue: 'Top performers' })}
          kicker={t('analyticsPage.topPerformersKicker', { defaultValue: 'By total points' })}
          icon={Award}
          iconClass="text-primary-100"
        >
          {top_performers?.length > 0 ? (
            <div className="space-y-3">
              {top_performers.map((user, idx) => (
                <div
                  key={user.user_id}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.04] border border-[var(--color-border)]"
                  data-testid={`top-performer-${idx}`}
                >
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center text-sm font-display',
                      idx === 0
                        ? 'bg-gradient-to-br from-primary-500 to-primary-300 text-[#0a1208]'
                        : idx === 1
                        ? 'bg-white/[0.12] text-[var(--color-text)]'
                        : idx === 2
                        ? 'bg-white/[0.08] text-[var(--color-text)]'
                        : 'bg-white/[0.04] text-[var(--color-text-muted)]'
                    )}
                  >
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[var(--color-text)] truncate">{user.user_name}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">{user.metric_name}</p>
                  </div>
                  <div className="font-display text-lg text-primary-100">
                    {Math.round(user.metric_value).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyMini
              icon={Award}
              text={t('analyticsPage.topPerformersEmpty', { defaultValue: 'No performer data' })}
            />
          )}
        </EditorialPanel>
      </div>

      {/* Trainer Performance Table */}
      <EditorialPanel
        title={t('analyticsPage.trainerPerf', { defaultValue: 'Trainer performance' })}
        kicker={t('analyticsPage.trainerPerfKicker', { defaultValue: 'Ranked by sessions' })}
        icon={Target}
        iconClass="text-primary-100"
        padded={false}
      >
        {trainer_performance?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] font-normal">
                    #
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] font-normal">
                    {t('analyticsPage.trainerCol', { defaultValue: 'Trainer' })}
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] font-normal">
                    {t('analyticsPage.clientsCol', { defaultValue: 'Clients' })}
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] font-normal">
                    {t('analyticsPage.sessionsCol', { defaultValue: 'Sessions' })}
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] font-normal">
                    {t('analyticsPage.confirmedCol', { defaultValue: 'Confirmed' })}
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] font-normal">
                    {t('analyticsPage.pointsAwardedCol', { defaultValue: 'Points' })}
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] font-normal">
                    {t('analyticsPage.rateCol', { defaultValue: 'Rate' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {trainer_performance
                  .sort((a, b) => b.sessions_logged - a.sessions_logged)
                  .map((trainer, idx) => (
                    <tr
                      key={trainer.trainer_id}
                      className="border-b border-[var(--color-border)]/60 last:border-0 hover:bg-white/[0.03] transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div
                          className={cn(
                            'w-7 h-7 rounded-full flex items-center justify-center text-xs font-display',
                            idx === 0
                              ? 'bg-gradient-to-br from-primary-500 to-primary-300 text-[#0a1208]'
                              : 'bg-white/[0.04] text-[var(--color-text-muted)] border border-[var(--color-border)]'
                          )}
                        >
                          {idx + 1}
                        </div>
                      </td>
                      <td className="py-3 px-4 font-medium text-[var(--color-text)]">
                        {trainer.trainer_name}
                      </td>
                      <td className="py-3 px-4 text-[var(--color-text-muted)]">{trainer.total_clients}</td>
                      <td className="py-3 px-4 text-[var(--color-text-muted)]">{trainer.sessions_logged}</td>
                      <td className="py-3 px-4 text-[var(--color-text-muted)]">{trainer.sessions_confirmed}</td>
                      <td className="py-3 px-4 text-primary-100 font-display">
                        {trainer.total_points_awarded?.toLocaleString() || 0}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={cn(
                            'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border',
                            trainer.confirmation_rate >= 80
                              ? 'border-primary-200/30 bg-primary-200/10 text-primary-100'
                              : trainer.confirmation_rate >= 50
                              ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                              : 'border-red-500/30 bg-red-500/10 text-red-200'
                          )}
                        >
                          {trainer.confirmation_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-8 text-center text-[var(--color-text-muted)] text-sm">
            {t('analyticsPage.trainerEmpty', { defaultValue: 'No trainer data available' })}
          </div>
        )}
      </EditorialPanel>

      {/* Footer */}
      <div className="text-center text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] py-4">
        {t('analyticsPage.generatedAt', { defaultValue: 'Data last updated' })}:{' '}
        {new Date(analytics.generated_at).toLocaleString()}
      </div>
    </div>
  );
};

// ===========================================================================
// Sub-components
// ===========================================================================

function Loader2({ className }) {
  return (
    <svg
      className={cn('animate-spin', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function EditorialPanel({ title, kicker, icon: Icon, iconClass, children, padded = true }) {
  return (
    <div
      className={cn('editorial-panel', padded && 'p-6')}
      style={{
        background: 'rgba(8, 13, 11, 0.5)',
        border: '1px solid var(--color-border)',
        borderRadius: '1rem',
      }}
    >
      <div className={cn('flex items-center justify-between', padded ? 'mb-4' : 'px-6 pt-6')}>
        <div>
          {kicker && <span className="hero-kicker">{kicker}</span>}
          <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">{title}</h3>
        </div>
        {Icon && <Icon className={cn('w-5 h-5', iconClass)} />}
      </div>
      <div className={cn(!padded && 'px-6 pb-6')}>{children}</div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, trend, trendValue, color = 'primary' }) {
  const colorClasses = {
    primary: 'border-[rgba(213,255,123,0.18)] bg-[rgba(213,255,123,0.05)] text-primary-100',
    blue: 'border-[rgba(95,180,255,0.18)] bg-[rgba(95,180,255,0.05)] text-blue-200',
    purple: 'border-[rgba(195,140,255,0.18)] bg-[rgba(195,140,255,0.05)] text-purple-200',
    amber: 'border-[rgba(255,210,125,0.18)] bg-[rgba(255,210,125,0.05)] text-amber-200',
    green: 'border-[rgba(120,235,160,0.18)] bg-[rgba(120,235,160,0.05)] text-green-200',
    red: 'border-[rgba(255,120,120,0.18)] bg-[rgba(255,120,120,0.05)] text-red-200',
  };

  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-5 hover:bg-white/[0.03] transition-colors"
    >
      <div className="flex items-start justify-between">
        <div className={cn('p-2.5 rounded-xl border', colorClasses[color])}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <div
            className={cn(
              'flex items-center gap-1 text-sm font-medium',
              trend === 'up' ? 'text-primary-100' : 'text-red-200'
            )}
          >
            {trend === 'up' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {trendValue}%
          </div>
        )}
      </div>
      <div className="mt-3">
        <p className="font-display text-3xl text-[var(--color-text)]">{value}</p>
        <p className="text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mt-1">{label}</p>
      </div>
    </div>
  );
}

function MetricBar({ label, value, percent, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-gradient-to-r from-primary-500 to-primary-300',
    blue: 'bg-gradient-to-r from-blue-500 to-blue-300',
    purple: 'bg-gradient-to-r from-purple-500 to-purple-300',
    amber: 'bg-gradient-to-r from-amber-500 to-amber-300',
  };
  return (
    <div>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-[var(--color-text-muted)]">{label}</span>
        <span className="font-display text-[var(--color-text)]">{value}</span>
      </div>
      <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden border border-[var(--color-border)]">
        <div
          className={cn('h-full rounded-full transition-all duration-500', colorClasses[color])}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
}

function BarChart({ data, dataKey, labelKey, color = 'primary', height = 200 }) {
  if (!data || data.length === 0) {
    return <EmptyMini icon={BarChart3} text="No data" />;
  }
  const maxValue = Math.max(...data.map((d) => d[dataKey] || 0));
  const colorClasses = {
    primary: 'from-primary-500 to-primary-300 hover:from-primary-400 hover:to-primary-200',
    blue: 'from-blue-500 to-blue-300 hover:from-blue-400 hover:to-blue-200',
    purple: 'from-purple-500 to-purple-300 hover:from-purple-400 hover:to-purple-200',
    amber: 'from-amber-500 to-amber-300 hover:from-amber-400 hover:to-amber-200',
  };

  return (
    <div className="flex items-end gap-1.5" style={{ height }}>
      {data.slice(-14).map((entry, idx) => {
        const value = entry[dataKey] || 0;
        const barHeight = maxValue > 0 ? (value / maxValue) * 100 : 0;
        const label = entry[labelKey] || '';
        return (
          <div key={idx} className="flex-1 flex flex-col items-center group">
            <div className="relative w-full">
              <div
                className={cn(
                  'w-full rounded-t bg-gradient-to-t transition-all duration-300 cursor-pointer',
                  colorClasses[color] || colorClasses.primary
                )}
                style={{ height: `${Math.max(barHeight, 2)}%`, minHeight: '4px' }}
              />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-[#0a1208] border border-[var(--color-border)] text-[var(--color-text)] text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                {value.toLocaleString()}
              </div>
            </div>
            <span className="text-[10px] text-[var(--color-text-muted)] mt-1 transform -rotate-45 origin-left whitespace-nowrap">
              {label.slice(-5)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({ data, size = 160 }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((acc, d) => acc + d.count, 0);
  const colors = ['#d5ff7b', '#5fb4ff', '#c38cff', '#ffd27d', '#ff8a8a', '#9aa5a0'];
  const segments = data.map((segment, idx) => {
    const prevAngles = data.slice(0, idx).reduce((acc, d) => acc + (d.count / total) * 360, 0);
    const angle = (segment.count / total) * 360;
    return { ...segment, startAngle: prevAngles, angle };
  });

  return (
    <div className="flex items-center gap-6">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((segment, idx) => {
          const endAngle = segment.startAngle + segment.angle;
          const startRad = (segment.startAngle - 90) * Math.PI / 180;
          const endRad = (endAngle - 90) * Math.PI / 180;
          const radius = size / 2 - 10;
          const innerRadius = radius * 0.6;
          const cx = size / 2;
          const cy = size / 2;
          const x1 = cx + radius * Math.cos(startRad);
          const y1 = cy + radius * Math.sin(startRad);
          const x2 = cx + radius * Math.cos(endRad);
          const y2 = cy + radius * Math.sin(endRad);
          const x3 = cx + innerRadius * Math.cos(endRad);
          const y3 = cy + innerRadius * Math.sin(endRad);
          const x4 = cx + innerRadius * Math.cos(startRad);
          const y4 = cy + innerRadius * Math.sin(startRad);
          const largeArc = segment.angle > 180 ? 1 : 0;
          return (
            <path
              key={idx}
              d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`}
              fill={colors[idx % colors.length]}
              className="hover:opacity-80 transition-opacity cursor-pointer"
            />
          );
        })}
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dy="0.3em"
          className="text-xl font-display fill-[var(--color-text)]"
        >
          {total}
        </text>
      </svg>
      <div className="space-y-2 flex-1 min-w-0">
        {data.map((segment, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: colors[idx % colors.length] }}
            />
            <span className="text-[var(--color-text-muted)] truncate">{segment.session_type}</span>
            <span className="ml-auto font-display text-[var(--color-text)]">{segment.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PeriodSelector({ value, onChange, t }) {
  const options = [
    { value: 7, label: t('analyticsPage.days7', { defaultValue: '7 d' }) },
    { value: 30, label: t('analyticsPage.days30', { defaultValue: '30 d' }) },
    { value: 90, label: t('analyticsPage.days90', { defaultValue: '90 d' }) },
    { value: 365, label: t('analyticsPage.days365', { defaultValue: '1 y' }) },
  ];
  return (
    <div
      className="inline-flex p-1 rounded-2xl border border-[var(--color-border)] bg-white/[0.04]"
      role="tablist"
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors',
              active
                ? 'bg-primary-300 text-[#0a1208]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            )}
            data-testid={`period-${o.value}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function EmptyMini({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center h-32 text-[var(--color-text-muted)]">
      <Icon className="w-8 h-8 mb-2 opacity-60" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

function ErrorState({ message, onRetry, t }) {
  return (
    <div
      className="rounded-2xl border border-red-500/30 bg-red-500/10 text-red-200 p-4 flex items-center gap-3"
      data-testid="analytics-error"
    >
      <AlertCircle className="w-5 h-5 shrink-0" />
      <p className="text-sm flex-1">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-xs font-semibold hover:bg-white/[0.08] transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {t('analyticsPage.retry', { defaultValue: 'Retry' })}
      </button>
    </div>
  );
}

export default AnalyticsPage;
